// hooks/meetings.ts
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

/** --- Types --- */

export type RecurringPattern = {
  daysOfWeek: string[];
};

export type Organizer = {
  _id: string;
  email: string;
};

export type AttendeeUser = {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  createdAt?: string;
  updatedAt?: string;
  org?: string;
  position?: string;
  role?: string;
  // add other user fields as needed
};

export type MeetingAttendeeFull = {
  user: AttendeeUser;
  userType: string;
  status?: string;
  _id: string;
};

export type ProjectRef = {
  _id: string;
  name?: string;
};

export type Reminder = {
  type: string;
  time: number;
  sent: boolean;
  _id?: string;
};

export type MeetingDetails = {
  recurringPattern?: RecurringPattern;
  _id: string;
  title: string;
  description?: string;
  organizer?: Organizer;
  attendees: MeetingAttendeeFull[];
  scheduledDate: string;
  duration: number;
  status?: string;
  meetingPlatform?: string;
  projectId?: ProjectRef | string | null;
  organizationId?: string;
  agenda?: string;
  isRecurring?: boolean;
  reminders?: Reminder[];
  attachments?: any[];
  createdAt?: string;
  updatedAt?: string;
  meetingType?: string;
  endTime?: string;
  id?: string;
};

export type MeetingGetResponse = {
  success: boolean;
  data: MeetingDetails;
};

const getMeeting = async (meetingId: string): Promise<MeetingGetResponse> => {
  const { data } = await customAxios.get<MeetingGetResponse>(
    `/meetings/${meetingId}`
  );
  return data;
};

export const useGetMeeting = (meetingId?: string) => {
  const queryClient = useQueryClient();

  return useQuery<MeetingGetResponse, AxiosError>(
    ["meeting", meetingId],
    () => {
      if (!meetingId) throw new Error("meetingId is required");
      return getMeeting(meetingId);
    },
    {
      enabled: !!meetingId,
      staleTime: 0,
      onError: (error) =>
        showNotification({
          title: "Error",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to fetch meeting details",
          color: "red",
        }),
      onSuccess: (data) => {},
    }
  );
};
